import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { locationHubService } from '../../services/locationHubService.js';
import { ArrowLeft, IndianRupee, Users, MapPin, Wallet } from 'lucide-react';
import Button from '../common/Button.js';
import {
  AnalyticsStatCard,
  AnalyticsTable,
  analyticsStatsGrid4Class,
  useAnalyticsCompanyScope,
  buildAnalyticsParams,
} from './common/index.js';
import { hasMlAgentCommissionAccess } from '../../utils/networkMode.js';
import { useAuth } from '../../context/AuthContext.js';
import { formatHubBranchLabel, filterShipmentLocations } from '../../utils/destinationType.js';
import { formatCurrencyWhole as formatCurrency } from '../../utils/formatCurrency.js';

const CommissionAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const [agentUserId, setAgentUserId] = useState('');
  const [locationHubId, setLocationHubId] = useState('');
  const [status, setStatus] = useState('');
  const [locations, setLocations] = useState([]);
  const [expandedHub, setExpandedHub] = useState(null);
  const {
    currentUser,
    companies,
    selectedCompany,
    handleCompanyChange,
    ready,
  } = useAnalyticsCompanyScope({ filter, customDates });

  useEffect(() => {
    if (!hasMlAgentCommissionAccess(user)) {
      navigate('/analytics', { replace: true });
      return;
    }
    locationHubService.getAllHubs(null, null, { all: true })
      .then((d) => {
        const raw = d?.data ?? d;
        const list = Array.isArray(raw) ? raw : (raw?.hubs || raw?.location_hubs || []);
        setLocations(Array.isArray(list) ? list : []);
      })
      .catch(() => setLocations([]));
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildAnalyticsParams({
        filter,
        customDates,
        selectedCompany,
        currentUser,
        extra: {
          ...(agentUserId ? { agent_user_id: agentUserId } : {}),
          ...(locationHubId ? { location_hub_id: locationHubId } : {}),
          ...(status ? { status } : {}),
        },
      });
      const response = await analyticsService.getCommissionAnalytics(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching commission analytics:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filter, customDates, agentUserId, locationHubId, status, selectedCompany, currentUser]);

  useEffect(() => {
    if (!hasMlAgentCommissionAccess(user) || !ready) return;
    fetchData();
  }, [fetchData, user, ready]);

  const locationOptions = filterShipmentLocations(locations || []).map((l) => ({
    value: String(l.id),
    label: formatHubBranchLabel(l) || l.location || '',
  }));

  const agentOptions = (data?.agents || []).map((a) => ({
    value: a.user_id,
    label: `${a.name || a.user_id} (${a.user_id})`,
  }));

  if (loading && !data) {
    return (
      <DashboardLayout fullWidth>
        <Loader fullScreen={false} size="default" message="Loading Commission Analytics..." />
      </DashboardLayout>
    );
  }

  const summary = data?.summary || {};

  return (
    <DashboardLayout fullWidth>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-xl font-semibold text-secondary">Shipment Commission Analytics</h1>
            <p className="text-sm text-gray-600">ML agent commission by hub, branch, and agent</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <DateFilterDropdown
              filter={filter}
              setFilter={setFilter}
              customDates={customDates}
              setCustomDates={setCustomDates}
              onApply={fetchData}
              dateRange={data?.filter}
              currentUser={currentUser}
              companies={companies}
              selectedCompany={selectedCompany}
              onCompanyChange={handleCompanyChange}
              showCompanyFilter
            />
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6 flex flex-col sm:flex-row gap-3 flex-wrap">
          <select
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm min-w-[180px]"
            value={agentUserId}
            onChange={(e) => setAgentUserId(e.target.value)}
          >
            <option value="">All Agents</option>
            {agentOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm min-w-[180px]"
            value={locationHubId}
            onChange={(e) => setLocationHubId(e.target.value)}
          >
            <option value="">All Hubs / Branches</option>
            {locationOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm min-w-[140px]"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PAID">Paid</option>
          </select>
          <Button variant="secondary" onClick={fetchData}>Apply Filters</Button>
        </div>

        {data && (
          <>
            <div className={`${analyticsStatsGrid4Class} mb-6`}>
              <AnalyticsStatCard
                title="Total Commission"
                value={formatCurrency(summary.commission_amount)}
                subtitle={`${summary.entry_count || 0} entries${summary.tds_amount ? ` · TDS ${formatCurrency(summary.tds_amount)}` : ''}`}
                icon={IndianRupee}
                color="bg-orange-100 text-orange-600"
              />
              <AnalyticsStatCard
                title="Unpaid"
                value={formatCurrency(summary.unpaid_amount)}
                subtitle={`${summary.unpaid_count || 0} entries`}
                icon={Wallet}
                color="bg-rose-100 text-rose-600"
              />
              <AnalyticsStatCard
                title="Paid"
                value={formatCurrency(summary.paid_amount)}
                subtitle={`${summary.paid_count || 0} entries`}
                icon={IndianRupee}
                color="bg-emerald-100 text-emerald-600"
              />
              <AnalyticsStatCard
                title="Freight Base"
                value={formatCurrency(summary.freight_base)}
                subtitle="Commissionable freight"
                icon={MapPin}
                color="bg-sky-100 text-sky-600"
              />
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                <div className="flex items-center mb-4">
                  <MapPin className="w-5 h-5 text-sky-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-800">By Hub</h2>
                </div>
                <AnalyticsTable
                  columns={[
                    { key: 'hub', header: 'Hub' },
                    { key: 'entries', header: 'Entries', align: 'right' },
                    { key: 'commission', header: 'Commission', align: 'right' },
                    { key: 'unpaid', header: 'Unpaid', align: 'right' },
                  ]}
                  rows={(data.hubs_summary || []).map((r) => ({
                    key: r.hub_name,
                    cells: [
                      r.hub_name,
                      r.entry_count,
                      formatCurrency(r.commission_amount),
                      formatCurrency(r.unpaid_amount),
                    ],
                  }))}
                  emptyMessage="No hub data"
                />
              </div>

              <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                <div className="flex items-center mb-4">
                  <Users className="w-5 h-5 text-amber-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-800">By Agent</h2>
                </div>
                <AnalyticsTable
                  columns={[
                    { key: 'agent', header: 'Agent' },
                    { key: 'entries', header: 'Entries', align: 'right' },
                    { key: 'commission', header: 'Commission', align: 'right' },
                    { key: 'unpaid', header: 'Unpaid', align: 'right' },
                  ]}
                  rows={(data.agents_summary || []).map((r) => ({
                    key: r.agent_user_id,
                    cells: [
                      r.agent_name,
                      r.entry_count,
                      formatCurrency(r.commission_amount),
                      formatCurrency(r.unpaid_amount),
                    ],
                  }))}
                  emptyMessage="No agent data"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 lg:p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Hub → Branch → Agent</h2>
              <div className="space-y-3">
                {(data.hubs || []).length === 0 && (
                  <p className="text-sm text-slate-500">No commission entries for this filter.</p>
                )}
                {(data.hubs || []).map((hub) => {
                  const open = expandedHub === hub.hub_name;
                  return (
                    <div key={hub.hub_name} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-4 py-3 bg-sky-50 text-left"
                        onClick={() => setExpandedHub(open ? null : hub.hub_name)}
                      >
                        <span className="font-semibold text-slate-800">{hub.hub_name}</span>
                        <span className="text-sm text-slate-600">
                          {hub.totals.entry_count} · {formatCurrency(hub.totals.commission_amount)}
                          {' · unpaid '}{formatCurrency(hub.totals.unpaid_amount)}
                        </span>
                      </button>
                      {open && (
                        <div className="p-3 space-y-3">
                          {(hub.branches || []).map((branch) => (
                            <div key={branch.branch_name} className="pl-2 border-l-2 border-emerald-200">
                              <div className="flex justify-between text-sm font-medium text-emerald-800 mb-2">
                                <span>Branch: {branch.branch_name}</span>
                                <span>{formatCurrency(branch.totals.commission_amount)}</span>
                              </div>
                              {(branch.agents || []).map((agent) => (
                                <div key={agent.agent_user_id} className="mb-3 ml-2">
                                  <div className="flex justify-between text-sm text-amber-900 mb-1">
                                    <span>{agent.agent_name} ({agent.agent_user_id})</span>
                                    <span>
                                      {formatCurrency(agent.totals.commission_amount)}
                                      {' · U '}{formatCurrency(agent.totals.unpaid_amount)}
                                    </span>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full text-xs">
                                      <thead>
                                        <tr className="text-slate-500 border-b">
                                          <th className="text-left py-1 pr-2">ML Sheet</th>
                                          <th className="text-left py-1 pr-2">Side</th>
                                          <th className="text-right py-1 pr-2">Freight</th>
                                          <th className="text-right py-1 pr-2">%</th>
                                          <th className="text-right py-1 pr-2">Gross</th>
                                          <th className="text-right py-1 pr-2">TDS</th>
                                          <th className="text-right py-1 pr-2">Net</th>
                                          <th className="text-left py-1">Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(agent.entries || []).map((e) => (
                                          <tr key={e.id} className="border-b border-slate-50">
                                            <td className="py-1 pr-2">{e.ml_sheet_id}</td>
                                            <td className="py-1 pr-2 capitalize">{e.side}</td>
                                            <td className="py-1 pr-2 text-right">{formatCurrency(e.freight_base)}</td>
                                            <td className="py-1 pr-2 text-right">{e.commission_pct}</td>
                                            <td className="py-1 pr-2 text-right font-medium">{formatCurrency(e.commission_amount)}</td>
                                            <td className="py-1 pr-2 text-right">{formatCurrency(e.tds_amount || 0)}</td>
                                            <td className="py-1 pr-2 text-right font-medium">{formatCurrency(e.net_amount != null ? e.net_amount : e.commission_amount)}</td>
                                            <td className={`py-1 ${e.status === 'PAID' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                              {e.status}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CommissionAnalytics;
